'use client';

import { useId, useSyncExternalStore } from 'react';
import type { PriceEvent } from '@/lib/api';
import { buildJourney } from './priceJourney';

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

function reduceQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(REDUCE_QUERY);
}

function subscribeToMotionPreference(onChange: () => void): () => void {
  const mq = reduceQuery();
  if (!mq || typeof mq.addEventListener !== 'function') return () => {};
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/**
 * Whether this viewer has NOT asked for reduced motion.
 *
 * **Motion is opt-in here, which is the opposite of the usual shape.** The
 * common `matches ? off : on` treats "I could not ask" as consent. That state is
 * real, not hypothetical: jsdom in this repo's vitest setup does not define
 * `window.matchMedia` at all (measured — a probe asserting `'function'` failed
 * with `'undefined'`), and neither do some embedded webviews. So a missing API
 * resolves to STILL.
 */
function motionAllowed(): boolean {
  const mq = reduceQuery();
  return mq ? !mq.matches : false;
}

/** During SSR nobody has been asked yet, so the HTML ships unanimated. */
function motionAllowedOnServer(): boolean {
  return false;
}

/**
 * The keyframes and the classes that use them.
 *
 * They live here rather than in `globals.css` because `@theme` is the design
 * system's source of truth and this is one component's flourish, not a token.
 * No animation dependency: a dash-offset sweep and an opacity fade are two
 * keyframes, and the bundle budget (941/1200 KB) is not worth spending on a
 * library to express them.
 *
 * `pathLength="1"` on the path normalises its length to 1 user unit, so
 * `stroke-dasharray: 1` is the whole line whatever its real geometry — no
 * `getTotalLength()` measurement, no layout read, nothing that needs the element
 * to exist first.
 *
 * The easing is the system's: cubic-bezier(.16, 1, .3, 1), the same curve
 * `--animate-fade-up` uses.
 *
 * **The `@media` block is the second of three independent brakes**, not the
 * primary one. In order: (1) `animate` is false until JS has affirmatively
 * confirmed the viewer has NOT asked for reduced motion, so no `--draw` class is
 * ever emitted for someone who has (that is the one a test can drive, and
 * `PriceJourney.test.tsx` drives it); (2) this block, which holds even if the
 * class is somehow applied; (3) the global `prefers-reduced-motion` rule in
 * `globals.css @layer base`, which clamps every animation on the site to 0.01ms.
 * Any one of the three is sufficient. Motion on this page is not a preference we
 * get to override — people read it while anxious about the largest purchase of
 * their lives.
 */
const DRAW_CSS = `
@keyframes gr-pj-sweep { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
@keyframes gr-pj-rise { from { opacity: 0; } to { opacity: 1; } }
@keyframes gr-pj-pop { from { opacity: 0; transform: scale(0.35); } to { opacity: 1; transform: scale(1); } }
.gr-pj-line--draw { stroke-dasharray: 1; animation: gr-pj-sweep 900ms cubic-bezier(.16, 1, .3, 1) both; }
.gr-pj-area--draw { animation: gr-pj-rise 700ms cubic-bezier(.16, 1, .3, 1) 250ms both; }
.gr-pj-mark--draw { transform-box: fill-box; transform-origin: center; animation: gr-pj-pop 420ms cubic-bezier(.16, 1, .3, 1) 780ms both; }
@media (prefers-reduced-motion: reduce) {
  .gr-pj-line--draw, .gr-pj-area--draw, .gr-pj-mark--draw {
    animation: none;
    stroke-dasharray: none;
    opacity: 1;
    transform: none;
  }
}
`;

/**
 * The sparkline itself: emerald trend line, soft emerald wash beneath it, and a
 * bronze dot on the price the home is asking today.
 *
 * **The colours carry meaning and are not interchangeable.** Gold is this design
 * system's money colour, so it marks the one point on the chart that is a price
 * a buyer can act on. The line is a *trend*, not money, so it is emerald — the
 * accent. Coral is the error colour, and it marks the steps this feed should not
 * be believed about (see `MAX_CREDIBLE_CUT_PERCENT`).
 *
 * They are applied as `stroke-*` / `fill-*` **utilities**, not as
 * `stroke="var(--color-emerald-500)"` presentation attributes. A `var()` inside
 * a presentation attribute is not uniformly substituted, and the failure mode is
 * the worst one available here: the stroke resolves to nothing and the chart
 * renders as an empty box that reports no error at all. Utilities are ordinary
 * CSS, and the classes were checked in the built stylesheet
 * (`.stroke-emerald-500`, `.fill-gold-500`, `.aspect-\[4\/1\]{aspect-ratio:4}`)
 * — a class Tailwind never emitted would be the same invisible failure.
 *
 * A client component only so that the draw-on can be gated on the viewer's
 * motion preference. It still server-renders: `animate` starts false, so the
 * HTML that ships contains the finished, fully visible chart and the animation
 * is added afterwards, if at all. A viewer with JS off, or a hydration that
 * never happens, sees a correct chart — never an invisible one.
 */
export default function PriceJourneyChart({
  events,
  label,
}: {
  events: PriceEvent[];
  label: string;
}) {
  const gradientId = useId();
  // Subscribed, not sampled: a viewer who turns reduced motion ON mid-session is
  // answered on the next render rather than at the next full page load.
  const animate = useSyncExternalStore(
    subscribeToMotionPreference,
    motionAllowed,
    motionAllowedOnServer
  );

  const journey = buildJourney(events);
  if (!journey) return null;

  const { points, linePath, areaPath, suspectPaths, width, height } = journey;
  const current = points[points.length - 1];
  const draw = (base: string) => (animate ? `${base} ${base}--draw` : base);

  return (
    <div className="mt-4">
      <style>{DRAW_CSS}</style>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block w-full aspect-[4/1] overflow-visible"
        role="img"
        aria-label={label}
      >
        <defs>
          {/* `stop-color` has no Tailwind utility, so it goes through `style`
              — a real CSS declaration — rather than through a presentation
              attribute. Same reason as the strokes below. */}
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'var(--color-emerald-500)', stopOpacity: 0.2 }} />
            <stop
              offset="100%"
              style={{ stopColor: 'var(--color-emerald-500)', stopOpacity: 0.01 }}
            />
          </linearGradient>
        </defs>

        <path
          data-testid="price-journey-area"
          className={draw('gr-pj-area')}
          d={areaPath}
          fill={`url(#${gradientId})`}
          stroke="none"
        />

        <path
          data-testid="price-journey-line"
          className={`${draw('gr-pj-line')} stroke-emerald-500`}
          d={linePath}
          pathLength={1}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* Implausible steps, over the trend line: dashed and coral, so the eye
            reads "this segment is not to be trusted" before it reads the slope. */}
        {suspectPaths.map((d, i) => (
          <path
            key={i}
            data-testid="price-journey-suspect-segment"
            className="stroke-coral-400"
            d={d}
            fill="none"
            strokeWidth="2.5"
            strokeDasharray="6 5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* r=8, deliberately WIDER than the r=5.5 money dot drawn after it.
            When the flagged step is the most recent one — which is exactly the
            case a buyer is most likely to act on — both markers land on the same
            coordinates, and at r=5 the bronze dot covered the coral ring
            completely: the chart flagged the outlier everywhere except on the
            outlier. At r=8 the ring reads as a halo around the money dot. */}
        {points.map((p, i) =>
          p.suspect ? (
            <circle
              key={`s-${i}`}
              data-testid="price-journey-suspect-point"
              className="fill-canvas stroke-coral-400"
              cx={p.x}
              cy={p.y}
              r="8"
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
            />
          ) : null
        )}

        {/* The money marker: bronze, on today's asking price. */}
        <circle
          data-testid="price-journey-current-dot"
          className={`${draw('gr-pj-mark')} fill-gold-500 stroke-canvas`}
          cx={current.x}
          cy={current.y}
          r="5.5"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}
